"use client";

import { type RefObject, useEffect, useId, useRef } from "react";

// The Genie panel and an agent chat can each hold a card that answers to keys
// (an approval, a question). When focus is on no card, only the most recently
// shown one takes the keys, so one keystroke never answers two cards. Focus
// inside a card gives that card the keys. With focus on another control (the
// chain toggle just clicked) the newest takes only its choice keys: Enter and
// the arrows there belong to that control. Every card registers here, whatever
// it asks, so the rule holds across kinds.
const keyOwners: string[] = [];

export const COMPOSER_CARD_ATTRIBUTE = "data-composer-card";

// Controls that read keys themselves: a menu or select typeaheads on digits,
// so a "1" there must not also answer the card.
const KEY_TAKING_ROLES =
  '[role^="menu"],[role="listbox"],[role="option"],[role="combobox"],[role="slider"],[role="spinbutton"]';

export function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT" ||
    target.closest(KEY_TAKING_ROLES) !== null
  );
}

// Window-level keys for one card. ``onKey`` returns whether it used the key;
// ``isChoiceKey`` names the keys a card takes even while focus sits on another
// control. ``listening`` is false while the card's own text field has the keys.
export function useComposerKeys({
  containerRef,
  listening,
  onKey,
  isChoiceKey,
}: {
  containerRef: RefObject<HTMLElement | null>;
  listening: boolean;
  onKey: (e: KeyboardEvent) => boolean;
  isChoiceKey: (key: string) => boolean;
}): void {
  const ownerId = useId();
  const onKeyRef = useRef(onKey);
  onKeyRef.current = onKey;
  const isChoiceKeyRef = useRef(isChoiceKey);
  isChoiceKeyRef.current = isChoiceKey;

  useEffect(() => {
    keyOwners.push(ownerId);
    return () => {
      keyOwners.splice(keyOwners.indexOf(ownerId), 1);
    };
  }, [ownerId]);

  useEffect(() => {
    if (!listening) return;
    const onWindowKey = (e: KeyboardEvent) => {
      if (e.isComposing || isEditable(e.target)) return;
      const active = document.activeElement;
      const inside = !!active && !!containerRef.current?.contains(active);
      const inOtherCard =
        !inside && !!active?.closest(`[${COMPOSER_CARD_ATTRIBUTE}]`);
      const unfocused = !active || active === document.body;
      const isTopOwner = keyOwners.at(-1) === ownerId;
      if (inOtherCard) return;
      if (
        !inside &&
        !(isTopOwner && (unfocused || isChoiceKeyRef.current(e.key)))
      )
        return;
      if (onKeyRef.current(e)) e.preventDefault();
    };
    window.addEventListener("keydown", onWindowKey);
    return () => window.removeEventListener("keydown", onWindowKey);
  }, [listening, ownerId, containerRef]);
}
